import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { hybridUploadService } from "@/app/_module/services/hybridUploadService";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";

import type {
  ResourceType,
  CreateResourceRequest,
  ResourceCategory,
  ResourceFilters,
  UpdateResourceRequest,
} from "../types/types";
import { scanFileForSensitiveData } from "../utils/sensitiveDataScanner";

// Interface declarations (moved to follow ESLint rule)
interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
  timestamp: string;
  timeTakenMs: number;
  path: string;
}

interface CategoriesApiResponse {
  data: ResourceCategory[];
}

export const runtime = "nodejs";

export const resourcesService = {
  getResources: async (filters?: ResourceFilters): Promise<ResourceType[]> => {
    try {
      const params = new URLSearchParams();
      if (filters?.search) params.append("search", filters.search);
      if (filters?.carrier) params.append("carrier", filters.carrier);
      if (filters?.fileType) params.append("fileType", filters.fileType);
      if (filters?.category) params.append("category", filters.category);
      if (filters?.scope) params.append("scope", filters.scope);

      const queryString = params.toString();
      const url = queryString
        ? `${getApiUrl(endpoint.resources.getResources)}?${queryString}`
        : getApiUrl(endpoint.resources.getResources);

      const result = await handleApiCall<ApiResponse<{ data: ResourceType[] }>>(async () => {
        const response = await api.get<ApiResponse<{ data: ResourceType[] }>>({
          url,
          auth: true,
        });
        return response;
      });

      // Handle the standardized response structure from fixed backend
      // After handleApiCall, result is already unwrapped (just the data field)
      if (Array.isArray(result)) {
        // handleApiCall already extracted the data array
        return result;
      } else if (result && typeof result === 'object' && 'data' in result && Array.isArray((result as any).data)) {
        // Fallback: if response wasn't unwrapped, extract data field
        return (result as any).data;
      } else {

        return [];
      }
    } catch (error) {
      throw error;
    }
  },

  getResourceById: async (id: string): Promise<ResourceType> => {
    try {
      const result = await handleApiCall<ApiResponse<ResourceType>>(async () => {
        const response = await api.get<ApiResponse<ResourceType>>({
          url: getApiUrl(endpoint.resources.getResourceById(id)),
          auth: true,
        });
        return response;
      });

      if ((result as any).data && (result as any).data.data) {
        return (result as any).data.data;
      } else {
        throw new Error("Invalid response structure - resource data not found");
      }
    } catch (error) {
      throw error;
    }
  },

  getCategories: async (): Promise<ResourceCategory[]> => {
    try {
      const result = await handleApiCall<ApiResponse<CategoriesApiResponse>>(async () => {
        const response = await api.get<ApiResponse<CategoriesApiResponse>>({
          url: getApiUrl(endpoint.resources.getCategories),
          auth: true,
        });
        return response;
      });
      // handleApiCall unwraps the response, so result might be the data directly
      // Check multiple possible response structures
      if (Array.isArray(result)) {
        // Result is already the categories array
        return result;
      } else if (result.data && result.data.data && Array.isArray(result.data.data)) {
        // Nested structure: result.data.data
        return result.data.data;
      } else if (result.data && Array.isArray(result.data)) {
        // Single nesting: result.data
        return result.data;
      } else {

        return [];
      }
    } catch (error) {

      throw error;
    }
  },

  getAgencyCarriers: async (agencyId?: string): Promise<Array<{ id: string; name: string; slug?: string }>> => {
    try {
      // For agency members, ensure we only get their agency's carriers
      const url = agencyId 
        ? getApiUrl(`${endpoint.resources.getCarriers}?agencyId=${agencyId}`)
        : getApiUrl(endpoint.resources.getCarriers);
      
      const result = await handleApiCall(async () => {
        const response = await api.get({
          url,
          auth: true,
        });
        return response;
      });
      // Handle /agency-carriers endpoint response format
      // Response format: { statusCode, message, data: [{ carrierId, carrierName, carrierSlug, isEnabled, ... }] }
      let carriers: any[] = [];

      if (result.data && result.data.data && Array.isArray(result.data.data)) {
        carriers = result.data.data;
      } else if (result.data && Array.isArray(result.data)) {
        carriers = result.data;
      } else if (Array.isArray(result)) {
        carriers = result;
      } else {

        return [];
      }

      // Filter by agency if agencyId is provided
      if (agencyId) {
        carriers = carriers.filter((carrier: any) => 
          carrier.agencyId === agencyId || !carrier.agencyId
        );
      }

      // Filter enabled carriers and transform to expected format
      const enabledCarriers = carriers
        .filter((carrier: any) => carrier.isEnabled !== false) // Include if isEnabled is true or undefined
        .map((carrier: any) => ({
          id: carrier.carrierId || carrier.id || carrier.carrierSlug,
          name: carrier.carrierName || carrier.name,
          slug: carrier.carrierSlug || carrier.slug,
        }));
      return enabledCarriers;
    } catch (error) {

      // Return empty array on error to prevent breaking the UI
      return [];
    }
  },

  createResource: async (
    data: CreateResourceRequest & {
      onProgress?: (progress: number) => void;
      onStatusUpdate?: (isProcessing: boolean, message?: string) => void;
    }
  ): Promise<ResourceType | { isProcessing: boolean; uploadId: string; partialData: Partial<ResourceType> }> => {
    try {
      if (!data.file || !(data.file instanceof File)) {
        throw new Error("File is required and must be a valid File object");
      }

      // Scan for sensitive data BEFORE upload (client-side DLP)
      // This is necessary because hybrid upload sends files directly to Supabase,
      // so the backend never sees the file content
      let isSensitive = data.isSensitive || false;
      let sensitiveDataTypes = data.sensitiveDataTypes || [];
      let autoDetectedSensitive = false;

      // Only auto-scan if user hasn't already marked it as sensitive
      if (!data.isSensitive) {
        try {
          const scanResult = await scanFileForSensitiveData(data.file);
          if (scanResult.isSensitive) {
            isSensitive = true;
            sensitiveDataTypes = scanResult.detectedTypes;
            autoDetectedSensitive = true;
          }
        } catch (scanError) {
          // Don't block upload if scanning fails
          console.warn('Sensitive data scan failed:', scanError);
        }
      }

      // Use hybrid upload for immediate UI + fallback processing
      const uploadResult = await hybridUploadService.hybridUpload(
        data.file,
        'resources',
        data.onProgress,
        data.onStatusUpdate
      );

      if (uploadResult.verified && uploadResult.downloadUrl) {
        // Immediate verification successful - create resource now
        const resourceData = {
          title: data.title,
          description: data.description,
          categoryId: data.categoryId,
          carrier: data.carrier,
          fileType: data.fileType,
          fileUrl: uploadResult.downloadUrl,
          filePath: uploadResult.filePath,
          fileSize: uploadResult.fileSize,
          mimeType: uploadResult.mimeType,
          ...(data.tags && { tags: data.tags }),
          ...(data.scope && { scope: data.scope }),
          ...(data.featured !== undefined && { featured: data.featured }),
          // Sensitive info fields
          isSensitive,
          sensitiveDataTypes,
          autoDetectedSensitive,
        };

        const result = await handleApiCall<ApiResponse<ResourceType>>(async () => {
          const response = await api.post<ApiResponse<ResourceType>>({
            url: getApiUrl(endpoint.resources.createResource),
            body: resourceData,
            auth: true,
            options: {
              headers: {
                'Content-Type': 'application/json',
              },
            },
          });
          return response;
        });
        // handleApiCall might unwrap the response, so check multiple structures
        // Backend returns { statusCode, message, data, timestamp }
        if (result && typeof result === 'object' && 'id' in result) {
          // Result is already the resource object (unwrapped by handleApiCall)
          return result as unknown as ResourceType;
        } else if ((result as any).data && typeof (result as any).data === 'object' && 'id' in (result as any).data) {
          // Result still has the data wrapper
          return (result as any).data;
        } else {

          throw new Error("Invalid create response structure");
        }
      } else {
        // Upload is processing in background - return processing state
        return {
          isProcessing: true,
          uploadId: uploadResult.uploadId,
          partialData: {
            id: `processing-${uploadResult.uploadId}`,
            title: data.title,
            description: data.description,
            carrier: data.carrier,
            fileType: data.fileType,
            uploadedBy: 'You',
            downloadCount: 0,
            likeCount: 0,
            isLiked: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            categoryId: data.categoryId,
            filePath: uploadResult.filePath,
            uploadId: uploadResult.uploadId,
            status: 'PROCESSING',
            // Sensitive info fields
            isSensitive,
            sensitiveDataTypes,
            autoDetectedSensitive,
          }
        };
      }
    } catch (error) {
      // Handle upload errors with user-friendly messages
      if (error && typeof error === 'object' && 'message' in error) {
        hybridUploadService.handleUploadError(error);
      }
      throw error;
    }
  },

  updateResource: async (
    id: string,
    data: UpdateResourceRequest
  ): Promise<{ message: string }> => {
    try {
      const response = await api.put<ApiResponse<{ message: string }>>({
        url: getApiUrl(endpoint.resources.updateResource(id)),
        body: JSON.stringify(data),
        auth: true,
        options: {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      });

      if (response.data && response.data.data) {
        return response.data.data;
      } else {
        return { message: "Resource updated successfully" };
      }
    } catch (error) {
      throw error;
    }
  },

  deleteResource: async (
    id: string
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const result = await handleApiCall<ApiResponse<{ success: boolean; message: string }>>(async () => {
        const response = await api.delete<
          ApiResponse<{ success: boolean; message: string }>
        >({
          url: getApiUrl(endpoint.resources.deleteResource(id)),
          auth: true,
        });
        return response;
      });

      return (
        (result as any).data?.data || {
          success: true,
          message: "Resource deleted successfully",
        }
      );
    } catch (error) {
      throw error;
    }
  },

  likeResource: async (id: string): Promise<{ message: string }> => {
    try {
      const result = await handleApiCall<ApiResponse<{ message: string }>>(async () => {
        const response = await api.post<ApiResponse<{ message: string }>>({
          url: getApiUrl(endpoint.resources.likeResource(id)),
          auth: true,
        });
        return response;
      });

      return (result as any).data?.data || result;
    } catch (error) {
      throw error;
    }
  },

  unlikeResource: async (id: string): Promise<{ message: string }> => {
    try {
      const result = await handleApiCall<ApiResponse<{ message: string }>>(async () => {
        const response = await api.delete<ApiResponse<{ message: string }>>({
          url: getApiUrl(endpoint.resources.unlikeResource(id)),
          auth: true,
        });
        return response;
      });

      return (result as any).data?.data || result;
    } catch (error) {
      throw error;
    }
  },

  // Complete resource creation after background processing
  completeResourceCreation: async (
    uploadId: string,
    resourceData: {
      title: string;
      description?: string;
      categoryId: string;
      carrier: string;
      fileType: string;
      tags?: string[];
      visibility?: string;
      featured?: boolean;
      scope?: string;
      // Sensitive info fields
      isSensitive?: boolean;
      sensitiveDataTypes?: string[];
      autoDetectedSensitive?: boolean;
    },
    uploadStatus: {
      downloadUrl: string;
      filePath: string;
      fileSize?: number;
      mimeType?: string;
    }
  ): Promise<ResourceType> => {
    try {
      const completeResourceData = {
        ...resourceData,
        uploadId,
        fileUrl: uploadStatus.downloadUrl,
        filePath: uploadStatus.filePath,
        fileSize: uploadStatus.fileSize,
        mimeType: uploadStatus.mimeType
      };

      const result = await handleApiCall<ApiResponse<ResourceType>>(async () => {
        const response = await api.post<ApiResponse<ResourceType>>({
          url: getApiUrl(endpoint.resources.createResource),
          body: completeResourceData,
          auth: true,
          options: {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        });
        return response;
      });

      // handleApiCall might unwrap the response, so check multiple structures
      if (result && typeof result === 'object' && 'id' in result) {
        // Result is already the resource object (unwrapped by handleApiCall)
        return result as unknown as ResourceType;
      } else if ((result as any).data && typeof (result as any).data === 'object' && 'id' in (result as any).data) {
        // Result still has the data wrapper
        return (result as any).data;
      } else {

        throw new Error("Invalid create response structure");
      }
    } catch (error) {

      throw error;
    }
  },

  // Get view URL for a resource (uses backend endpoint for proper Content-Type handling)
  getViewUrl: (id: string): string => {
    return getApiUrl(endpoint.resources.viewResource(id));
  },

  // NEW: Track download count
  trackDownload: async (id: string): Promise<{ downloadCount: number; message: string }> => {
    try {
      const url = getApiUrl(endpoint.resources.trackDownload(id));

      const result = await handleApiCall<ApiResponse<{ downloadCount: number; message: string }>>(async () => {
        const response = await api.post<ApiResponse<{ downloadCount: number; message: string }>>({
          url,
          auth: true,
        });
        return response;
      });

      if ((result as any).data && (result as any).data.data) {
        return (result as any).data?.data || result;
      } else {
        // Fallback response if the endpoint doesn't return proper data
        return { downloadCount: 0, message: "Download tracked" };
      }
    } catch (error) {
      // If the endpoint doesn't exist, we can still continue with the download
      // but log the error for debugging

      // Return a default response so the download can continue
      return { downloadCount: 0, message: "Download tracking not available" };
    }
  },

  // Get sub-agency IDs for permission checks (all descendants of current agency)
  getSubAgencyIds: async (): Promise<string[]> => {
    try {
      const url = getApiUrl(endpoint.agency.getSubAgencies);

      const result = await handleApiCall<any>(async () => {
        const response = await api.get<any>({
          url,
          auth: true,
        });
        return response;
      });

      // Extract agency IDs from the response
      // Response structure: { data: { items: [...agencies] } } or { data: [...agencies] }
      let agencies: any[] = [];
      if (result?.data?.items && Array.isArray(result.data.items)) {
        agencies = result.data.items;
      } else if (result?.data && Array.isArray(result.data)) {
        agencies = result.data;
      } else if (Array.isArray(result)) {
        agencies = result;
      }

      // Recursively collect all agency IDs including nested sub-agencies
      const collectIds = (agencyList: any[]): string[] => {
        const ids: string[] = [];
        for (const agency of agencyList) {
          if (agency.id) {
            ids.push(agency.id);
          }
          if (agency.subAgencies && Array.isArray(agency.subAgencies)) {
            ids.push(...collectIds(agency.subAgencies));
          }
        }
        return ids;
      };

      return collectIds(agencies);
    } catch (error) {
      // Return empty array on error - user just won't have sub-agency permissions
      return [];
    }
  },
};